"use client";

import React from "react";
import { FarminstaIntroLayout } from "@/app/home/landing-page";
import { Button } from "@/components/ui/button";
import AnimatedSection from "@/app/home/animated-section";
import { Check, Star } from "lucide-react";
import Image from "next/image";
import heroSection from '../public/images/WebsiteImages/fcm/HeroSection.png'
import { useDemoModal } from "@/hooks/useDemoModal";


// Hero Section
const FFMHeroSection: React.FC = () => {

   const { openModal, DemoModal } = useDemoModal();
     
         const handleDemo = () => {
             openModal();
         }

  return (
    <>
    <section className="bg-[#ebf2f0] py-12 px-4 sm:px-6 lg:px-8 pt-20">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row items-center gap-12">
          {/* Left Content */}
          <div className="md:w-1/2">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Your field force, finally{" "}
              <span className="text-[#22A850]">on One Screen.</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 mb-6 leading-relaxed">
              Plan, execute, and close every agricultural season on a single platform.
            </p>
            <div className="border-l-4 border-[#22A850] pl-4 mb-8">
              <p className="text-base sm:text-lg text-gray-700">
              The operating system trusted by 20+ agri-input brands to manage across seasons —from annual planning in March to the final sales actuals posted at the end of the season.              </p>
            </div>
            <Button
              size="lg"
              className="bg-primary hover:bg-secondary text-white px-6 py-4 text-md rounded-sm font-bold transition duration-300 ease-in-out transform hover:scale-105"
              onClick={handleDemo}
            >
              Book a Demo
            </Button>
          </div>
          
          {/* Right Illustration */}
          <div className="md:w-1/2 flex justify-center">
             <div className="relative object-contain">
                      <Image
                        src={heroSection}
                        alt="Sales Dashboard Mockup"
                        className="rounded-lg object-contain h-[500px]"
                      />
                    </div>
          </div>
        </div>
      </div>
    </section>
    <DemoModal/>
    </>
  );
};

// Three Phases Section
const ThreePhasesSection: React.FC = () => {
  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Three Phases. One Operating System.
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From March planning to September close-out — Field Force Monitor runs your Kharif, Rabi, and Zaid on one platform.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Plan the Season */}
          <div className="bg-white rounded-2xl p-4 border-2 border-[#22A850]/20 shadow-lg hover:shadow-xl transition-shadow">
            <div className="">
              <div className="inline-block bg-[#22A850] text-white px-4 py-2 rounded-lg font-bold text-lg mb-4">
                Plan the Season
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900">Market, Plan & Blueprint</h4>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Village-level geography with hex-grid demand intelligence. Annual brand plan cascaded to territory, crop, variety, and village-level sales targets. Activity blueprint mapped by season phase: pre-launch, mid-season, harvest.
                </p>
              </div>
            </div>
          </div>

          {/* Run the Season */}
          <div className="bg-white rounded-2xl p-4 border-2 border-[#22A850]/20 shadow-lg hover:shadow-xl transition-shadow">
            <div className="">
              <div className="inline-block bg-[#22A850] text-white px-4 py-2 rounded-lg font-bold text-lg mb-4">
                Run the Season
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Targets, Routes, Scoring & Payroll</h4>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Activity, sales, and coverage targets cascaded weekly, monthly, season-long. Daily plans auto-generated, farmers prioritised, routes optimised. Composite performance scoring (Activity 40% + Achievement 30% + Coverage 15% + Connect 15%) refreshed daily, peer-ranked.
                </p>
              </div>
            </div>
          </div>

          {/* Close the Season */}
          <div className="bg-white rounded-2xl p-4 border-2 border-[#22A850]/20 shadow-lg hover:shadow-xl transition-shadow">
            <div className="">
              <div className="inline-block bg-[#22A850] text-white px-4 py-2 rounded-lg font-bold text-lg mb-4">
                Close the Season
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Activity, Farmer, Channel & Sales</h4>
                <p className="text-gray-600 text-sm leading-relaxed">
              Season-end reconciliation, not month-end guesswork. farmer records consolidated — crop, demo, commitment, engagement — owned and carried forward. Channel sell-out matched to sell-in, leakage exposed. Demos converted to sales actuals, village by village, so next season's plan starts from evidence.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Modules Section
const ModulesSection: React.FC = () => {
  const modules = [
    {
      title: "Field Officer HRMS",
      description: "Onboarding, attendance, daily plans, GPS-tagged activities, leave, statutory compliance, and payroll. Manage your field force from joining day to payroll day on a single system."
    },
    {
      title: "Farmer CRM",
      description: "Proprietary farmer database with crop profile, demo history, sales commitments, and engagement timeline. Segment, target, and follow up — your data, your relationship."
    },
    {
      title: "Demo Management",
      description: "Track every demo plot from sowing to yield across five stages. Stage progression, drop-off funnel, varietal performance, and commercial proof — by territory, by season."
    },
    {
      title: "Channel Management",
      description: "Retailer visits, stock updates, sales actuals, and farmer-level packet commitments — tracked together. Close the loop from sell-in commitment to sell-out at the counter."
    },
    {
      title: "Digital Marketing Hub",
      description: "Vernacular video, WhatsApp campaigns, regional influencer content, and farmer engagement reels. Run hyperlocal brand campaigns directly from the platform — no agency in the middle."
    },
    {
      title: "Intelligence Cockpit",
      description: "A unified view of your season — field force productivity, farmer engagement, demo lifecycle, channel performance, and commitment-vs-actual. Powered by Geo-Spatial Intelligence and the FO Co-Pilot AI assistant."
    }
  ];

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#ebf2f0]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
            The Modules Behind the Three Phases
          </h2>
          <p className="text-lg text-gray-600">
            One platform. Configured for your brand, your crops, your geography.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {modules.map((module, index) => (
            <div 
              key={index}
              className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-7 h-7 bg-[#22A850] rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-white stroke-[3]" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 leading-tight">
                  {module.title}
                </h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                {module.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// 21 Days Implementation Section
const ImplementationSection: React.FC = () => {
  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="mb-12 max-w-3xl">
          <p className="text-sm font-semibold text-[#22A850] uppercase tracking-wide mb-4">
            21-DAY IMPLEMENTATION
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 leading-tight max-w-2xl">
            Most enterprise software takes 6 months to go live. Farminsta gets you season-ready in three weeks.
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl">
            Because the platform is configured (not coded) to your operating model, deployment isn't a six-month custom build. It's a 21-day implementation — including hierarchy mapping, role configuration, FO onboarding, and the first live week of operations.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-[#22A850]/5 to-white rounded-2xl p-8 border border-gray-200">
            <div className="text-[#22A850] font-bold text-sm uppercase tracking-wide mb-4">
              WEEK 01 - CONFIGURE
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Your hierarchy, mapped</h3>
            <p className="text-gray-600 leading-relaxed">
              Roles assigned. Crop and season templates loaded. Approval workflows set to match your existing process.
            </p>
          </div>

          <div className="bg-gradient-to-br from-[#22A850]/5 to-white rounded-2xl p-8 border border-gray-200">
            <div className="text-[#22A850] font-bold text-sm uppercase tracking-wide mb-4">
              WEEK 02 - ONBOARD
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">FOs onboarded in batches</h3>
            <p className="text-gray-600 leading-relaxed">
              App installed, accounts provisioned, vernacular training delivered. ZMs and Sales Head trained on their respective views.
            </p>
          </div>

          <div className="bg-gradient-to-br from-[#22A850]/5 to-white rounded-2xl p-8 border border-gray-200">
            <div className="text-[#22A850] font-bold text-sm uppercase tracking-wide mb-4">
              WEEK 03 - GO LIVE
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">First full week of live ops</h3>
            <p className="text-gray-600 leading-relaxed">
              Daily activity reports flowing. Cockpit populated. Farminsta success team on standby for the first 14 days post-launch.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Social Proof Section
const SocialProofSection: React.FC = () => {
  return (
    <section className="py-12 px-4 sm:px-6 lg:px-8 bg-[#ebf2f0]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
            What our users say — at every level
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-8">
          {/* Google Play Store */}
          <div className="bg-white rounded-2xl p-8 shadow-md border border-gray-200">
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none">
                  <path d="M3 20.5V3.5C3 2.91 3.34 2.39 3.84 2.15L13.69 12L3.84 21.85C3.34 21.6 3 21.09 3 20.5Z" fill="#00D7FF"/>
                  <path d="M16.81 15.12L6.05 21.34L14.54 12.85L16.81 15.12Z" fill="#FFCE00"/>
                  <path d="M3.84 2.15C4.25 1.95 4.73 1.96 5.13 2.18L16.81 8.88L14.54 11.15L3.84 2.15Z" fill="#00F076"/>
                  <path d="M16.81 8.88L19.78 10.58C20.54 11.03 20.54 12.12 19.78 12.58L16.81 14.27L14.54 12L16.81 8.88Z" fill="#FF3E00"/>
                </svg>
              </div>
              <div className="flex-1">
                <div className="flex mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-2xl font-bold text-gray-900 mb-1">4.6 · Google Play</p>
                <p className="text-sm text-gray-500">Farminsta app · India</p>
              </div>
            </div>
          </div>

          {/* Apple App Store */}
          <div className="bg-white rounded-2xl p-8 shadow-md border border-gray-200">
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-10 h-10" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.1 22C7.79 22.05 6.8 20.68 5.96 19.47C4.25 17 2.94 12.45 4.7 9.39C5.57 7.87 7.13 6.91 8.82 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z" fill="#000000"/>
                </svg>
              </div>
              <div className="flex-1">
                <div className="flex mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-2xl font-bold text-gray-900 mb-1">4.7 · App Store</p>
                <p className="text-sm text-gray-500">Farminsta app · India</p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

// Final CTA Section
const FinalCTASection: React.FC = () => {

   const { openModal, DemoModal } = useDemoModal();
    
        const handleDemo = () => {
            openModal();
        }
  return (
    <>
    <section className="py-8 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
          Three weeks to live. One season to prove it.
        </h2>
        <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
          Book a 20-minute demo. We'll walk you through your planning canvas, your execution feed, and your closure cockpit — using your hierarchy, your crops, your geography.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
          <Button
            size="lg"
            className="bg-primary hover:bg-secondary text-white px-6 py-4 text-md rounded-sm font-bold transition duration-300 ease-in-out transform hover:scale-105"
            onClick={handleDemo}
          >
            Book a Demo
          </Button>
          {/* <Button 
            size="lg"
            variant="outline"
            className="border-2 border-[#22A850] text-[#22A850] hover:bg-[#22A850] hover:text-white px-8 py-6 text-lg"
          >
            Talk to a GTM Specialist
          </Button> */}
        </div>
      </div>
    </section>
      <DemoModal />
</>
  );
};

// Main Component
export function FieldForceMonitorContent() {
  return (
    <FarminstaIntroLayout currentPage="fieldForceMonitor">
      <FFMHeroSection />
      <AnimatedSection>
        <ThreePhasesSection />
      </AnimatedSection>
      <AnimatedSection>
        <ModulesSection />
      </AnimatedSection>
      <AnimatedSection>
        <ImplementationSection />
      </AnimatedSection>
      <AnimatedSection>
        <SocialProofSection />
      </AnimatedSection>
      <AnimatedSection>
        <FinalCTASection />
      </AnimatedSection>
    </FarminstaIntroLayout>
  );
}
